import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Switch, message, Select } from 'antd';
import request from '../../../utils/request';

type StepRow = {
  id?: number;
  step_key?: string;
  name?: string;
  version?: string;
  tags?: string[] | string;
  is_active?: boolean;
  default_required_metrics?: any;
  default_ui_schema?: any;
  default_action_plan?: any;
  updated_at?: string;
  created_at?: string;
};

const AdminStepLibraryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StepRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchKey, setSearchKey] = useState<string>('');
  const [searchActive, setSearchActive] = useState<'all'|'on'|'off'>('all');
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<StepRow | null>(null);
  const [form] = Form.useForm();

  const fetchList = async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: pageSize };
      if (searchKey.trim()) params.search = searchKey.trim();
      if (searchActive !== 'all') params.is_active = (searchActive === 'on');
      const resp = await request.get('/api/v1/instruction/steplibrary/', { params });
      const payload = resp?.data || {};
      const list = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      setRows(list);
      setTotal(Number(payload?.count || list?.length || 0));
    } catch {
      message.error('加载步骤库失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchList(); }, [page, pageSize, searchKey, searchActive]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, version: 'v1' });
    setEditOpen(true);
  };
  const openEdit = (row: StepRow) => {
    setEditing(row);
    form.setFieldsValue({
      step_key: row.step_key,
      name: row.name,
      version: row.version,
      tags: Array.isArray(row.tags) ? (row.tags as string[]).join(',') : (row.tags || ''),
      is_active: !!row.is_active,
      default_required_metrics: JSON.stringify(row.default_required_metrics || {}, null, 2),
      default_ui_schema: JSON.stringify(row.default_ui_schema || {}, null, 2),
      default_action_plan: JSON.stringify(row.default_action_plan || [], null, 2),
    });
    setEditOpen(true);
  };
  const removeRow = (row: StepRow) => {
    Modal.confirm({
      title: '确认删除该库步骤？',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await request.delete(`/api/v1/instruction/steplibrary/${row.id}/`);
          message.success('已删除');
          fetchList();
        } catch (e:any) {
          message.error(e?.response?.data?.detail || '删除失败');
        }
      }
    });
  };
  const toggleActive = async (row: StepRow, val: boolean) => {
    try {
      await request.patch(`/api/v1/instruction/steplibrary/${row.id}/`, { is_active: val });
      message.success('已更新');
      fetchList();
    } catch (e:any) {
      message.error(e?.response?.data?.detail || '更新失败');
    }
  };
  const saveEdit = async () => {
    try {
      const v = await form.validateFields();
      const payload: any = {
        step_key: String(v.step_key || '').trim(),
        name: String(v.name || '').trim(),
        version: String(v.version || 'v1').trim(),
        is_active: !!v.is_active,
        tags: String(v.tags || '').split(',').map((s:string)=> s.trim()).filter(Boolean),
      };
      // JSON 解析
      try { payload.default_required_metrics = v.default_required_metrics ? JSON.parse(String(v.default_required_metrics)) : {}; } catch { message.error('指标JSON不合法'); return; }
      try { payload.default_ui_schema = v.default_ui_schema ? JSON.parse(String(v.default_ui_schema)) : {}; } catch { message.error('UI配置JSON不合法'); return; }
      try { payload.default_action_plan = v.default_action_plan ? JSON.parse(String(v.default_action_plan)) : []; } catch { message.error('动作计划JSON不合法'); return; }
      if (editing?.id) {
        await request.patch(`/api/v1/instruction/steplibrary/${editing.id}/`, payload);
        message.success('已保存');
      } else {
        await request.post('/api/v1/instruction/steplibrary/', payload);
        message.success('已新增');
      }
      setEditOpen(false);
      fetchList();
    } catch {}
  };

  return (
    <div style={{ width: '100%' }}>
      <Card style={{ width: '100%' }} bodyStyle={{ padding: 12 }} headStyle={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div />
          <Space>
            <Input
              placeholder="搜索：key/名称/标签"
              allowClear
              style={{ width: 260 }}
              value={searchKey}
              onChange={(e)=> setSearchKey(e.target.value)}
            />
            <Select
              value={searchActive}
              style={{ width: 140 }}
              onChange={(v)=> setSearchActive(v as any)}
              options={[
                { value: 'all', label: '全部' },
                { value: 'on', label: '仅启用' },
                { value: 'off', label: '仅停用' },
              ]}
            />
            <Button onClick={fetchList}>刷新</Button>
            <Button type="primary" onClick={openCreate}>新建仓库操作步骤</Button>
          </Space>
        </div>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={{
            current: page, pageSize, total,
            showSizeChanger: true, showQuickJumper: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          }}
          columns={[
            { title: '步骤键', dataIndex: 'step_key', width: 160 },
            { title: '名称', dataIndex: 'name', width: 200 },
            { title: '版本', dataIndex: 'version', width: 100 },
            { title: '标签', dataIndex: 'tags', render: (v:any) => {
              const arr = Array.isArray(v) ? v : (typeof v === 'string' ? String(v).split(',') : []);
              return <Space wrap>{arr.map((t:string, i:number)=> <Tag key={i}>{t}</Tag>)}</Space>;
            }},
            { title: '启用', dataIndex: 'is_active', width: 100, render: (v:any, r:any) => <Switch checked={!!v} onChange={(ck)=> toggleActive(r, ck)} /> },
            { title: '更新时间', dataIndex: 'updated_at', width: 180 },
            { title: '操作', key: 'ops', width: 200, render: (_:any, r:any) => (
              <Space>
                <Button size="small" onClick={()=> openEdit(r)}>编辑</Button>
                <Button size="small" danger onClick={()=> removeRow(r)}>删除</Button>
              </Space>
            ) },
          ]}
        />
      </Card>
      <Modal
        title={editing?.id ? '编辑仓库操作步骤' : '新建仓库操作步骤'}
        open={editOpen}
        onCancel={()=> setEditOpen(false)}
        onOk={saveEdit}
        width={960}
      >
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 8 }}>
          <Button
            onClick={() => Modal.info({
              title: '填写说明（新建仓库操作步骤）',
              icon: null,
              centered: true,
              width: 960,
              okText: '知道了',
              content: (
                <div style={{ whiteSpace: 'pre-line', color: '#141414' }}>
{`字段说明与示例
1) 步骤键（step_key，必填，英文/编码，唯一）
   - 标识该标准步骤的编码，例如：PICK、BOX_PACK、PRINT_PICK_LIST、WEIGH、QC
   - 建议与系统中的 step_type 使用同一语义编码，便于引用与统计
   - 示例：BOX_PACK

2) 名称（name，必填，中文）
   - 人类可读名称，例如：装箱、拣货、打印拣货单、称重、质检拍照
   - 示例：装箱

3) 版本（version，选填）
   - 默认 v1；用于发布与升级治理，示例：v2、v2025.01 等

4) 标签（tags，选填）
   - 逗号分隔的标签列表，用于快速筛选，如：pick,pack,print
   - 示例：pick,process

5) 启用（is_active）
   - 启用后方可在模板中引用；停用不会影响已引用的模板历史数据

6) 指标定义（default_required_metrics，JSON）
   - 定义需要采集的业务指标字段与校验规则；键名应与计费规则口径或业务含义一致
   - 常见 type：number/string/array；array.item 可取 image/file/string
   - 示例（装箱）：
{
  "box_count":   { "type": "number", "label": "箱数", "required": true, "min": 0 },
  "volume_cc":   { "type": "number", "label": "体积(cc)", "required": false },
  "weight_g":    { "type": "number", "label": "重量(g)", "required": false },
  "remarks":     { "type": "string", "label": "备注" }
}

7) UI 配置（default_ui_schema，JSON）
   - 定义执行页“步骤子页面”的控件与布局，以及控件与指标的绑定（bind）
   - 常见控件：ScanInput / Number / Text / Select / PhotoUploader / DimensionsInput / WeightInput / TableInput 等
   - 示例（装箱）：
{
  "components": [
    { "type": "Number", "label": "箱数", "bind": "box_count", "min": 0, "required": true },
    { "type": "Number", "label": "体积(cc)", "bind": "volume_cc", "min": 0 },
    { "type": "Number", "label": "重量(g)", "bind": "weight_g", "min": 0 },
    { "type": "Text",   "label": "备注", "bind": "remarks", "placeholder": "可填写异常/说明" }
  ]
}

8) 动作计划（default_action_plan，JSON 数组）
   - 定义提交该步骤后，后端需要执行的动作（白名单动作 + 参数映射）
   - 参数模板变量：{{metrics.xxx}}（本步骤采集指标），{{instruction_id}}、{{user_id}}、{{step_id}}
   - 示例（装箱，更新箱信息）：
[
  { "action": "boxing.create_or_update",
    "args": {
      "box_count": "{{metrics.box_count}}",
      "volume_cc": "{{metrics.volume_cc}}",
      "weight_g":  "{{metrics.weight_g}}",
      "remarks":   "{{metrics.remarks}}"
    }
  }
]

使用建议
- 先在“步骤库”维护标准步骤（指标/UI/动作），模板中引用并按需少量覆写
- 真正复杂/高度交互的页面，可在 UI 配置中预留自定义控件（如 widget: "Custom:XXX"）逐步扩展`}
                </div>
              )
            })}
          >
            填写说明
          </Button>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="step_key" label="步骤键" rules={[{ required: true, message: '请输入步骤键（唯一）' }]}>
            <Input placeholder="如：PICK/BOX_PACK/PRINT_PICK_LIST" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="version" label="版本" style={{ flex: 1 }}>
              <Input placeholder="v1" />
            </Form.Item>
            <Form.Item name="tags" label="标签（逗号分隔）" style={{ flex: 2 }}>
              <Input placeholder="pick,pack,print" />
            </Form.Item>
            <Form.Item name="is_active" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="default_required_metrics" label="指标定义（JSON）" tooltip="用于校验提交的指标字段/类型/必填等">
            <Input.TextArea rows={6} placeholder='{"qty":{"type":"number","required":true}}' />
          </Form.Item>
          <Form.Item name="default_ui_schema" label="UI配置（JSON）" tooltip="定义前端渲染控件与布局，如扫描框/表单/表格等">
            <Input.TextArea rows={8} placeholder='{"components":[{"type":"ScanInput","label":"容器","bind":"bin_code"}]}' />
          </Form.Item>
          <Form.Item name="default_action_plan" label="动作计划（JSON）" tooltip="白名单动作与参数映射，支持 {{metrics.xxx}} 模板变量">
            <Input.TextArea rows={8} placeholder='[{"action":"inventory.move","args":{"from_container":"{{metrics.bin_code}}","quantity":"{{metrics.qty}}"}}]' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminStepLibraryPage;


